import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  CreditCard,
  BellRing,
  ListChecks,
  Inbox,
  Building2,
  ShieldCheck,
  Timer,
  MessageSquare,
  MessageCircle,
  Menu,
  X,
  Settings as SettingsIcon,
  LogOut,
  Building2 as WorkspaceIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { T } from "@/lib/testIds";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

const BASE_NAV = [
  { to: "/app/admin/workspaces", label: "All workspaces", icon: Building2, testId: T.navPlatformWs, roles: ["platform_admin"] },
  { to: "/app/admin/users", label: "All users", icon: ShieldCheck, testId: T.navPlatformUsers, roles: ["platform_admin"] },
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: T.navDashboard, roles: ["owner", "admin", "staff"] },
  { to: "/app/clients", label: "Clients", icon: Users, testId: T.navClients, roles: ["owner", "admin", "staff"] },
  { to: "/app/verify", label: "To verify", icon: Inbox, testId: T.navVerify, roles: ["owner", "admin"], badgeKey: "pending_review" },
  { to: "/app/reports/ageing", label: "Ageing report", icon: Timer, testId: T.navAgeing, roles: ["owner", "admin"] },
  { to: "/app/payment-options", label: "Payment options", icon: CreditCard, testId: T.navPaymentOptions, roles: ["owner", "admin"] },
  { to: "/app/reminder-rules", label: "Reminder rules", icon: ListChecks, testId: T.navReminderRules, roles: ["owner", "admin"] },
  { to: "/app/reminders", label: "Reminder log", icon: BellRing, testId: T.navReminders, roles: ["owner", "admin", "staff"] },
  { to: "/app/sms-templates", label: "SMS templates", icon: MessageSquare, testId: T.navSmsTemplates, roles: ["owner", "admin", "staff"] },
  { to: "/app/whatsapp", label: "WhatsApp inbox", icon: MessageCircle, testId: T.navWhatsapp, roles: ["owner", "admin", "staff"] },
  { to: "/app/team", label: "Team", icon: UserPlus, testId: T.navTeam, roles: ["owner", "admin"] },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testId: T.navSettings, roles: ["owner", "admin"] },
];

export default function AppLayout() {
  const { user, workspace, logout } = useAuth();
  const nav = useNavigate();
  const [badges, setBadges] = useState({ pending_review: 0 });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const impersonating = user?.role === "platform_admin" && localStorage.getItem("co_impersonate_ws");
  const impersonatingName = localStorage.getItem("co_impersonate_ws_name");

  const stopImpersonating = () => {
    localStorage.removeItem("co_impersonate_ws");
    localStorage.removeItem("co_impersonate_ws_name");
    window.location.href = "/app/admin/workspaces";
  };

  useEffect(() => {
    let cancelled = false;
    // Platform admin doesn't need the pending_review badge
    if (user?.role === "platform_admin") return;
    const tick = async () => {
      try {
        const r = await api.get("/dashboard/summary");
        if (!cancelled) setBadges({ pending_review: r.data?.kpis?.pending_review || 0 });
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  const doLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      {/* Sidebar - desktop static, mobile drawer */}
      <aside
        data-testid={T.sidebar}
        className={`flex-col border-r co-hairline bg-white z-50 lg:z-auto
          fixed inset-y-0 left-0 w-[280px] transform transition-transform duration-200 lg:transform-none
          lg:static lg:flex lg:w-auto
          ${mobileNavOpen ? "flex translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="px-6 py-6 border-b co-hairline">
          <div className="flex items-center gap-2.5">
            <img
              src="/collectone-logo.webp"
              alt="CollectOne"
              className="w-9 h-9 object-contain"
            />
            <div>
              <div className="font-heading text-lg font-bold tracking-tight leading-none">
                CollectOne
              </div>
              <div className="co-label mt-1">
                {workspace?.business_type === "distributor" ? "Distributor" : "Collector"}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-2">
          <div className="co-label mb-3">Workspace</div>
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} aria-hidden="true" />
            <span className="font-semibold truncate">{workspace?.name || "—"}</span>
          </div>
        </div>

        <nav className="flex-1 px-3 pt-6 space-y-1 overflow-y-auto">
          {BASE_NAV.filter((n) => n.roles.includes(user?.role || "owner")).map((n) => {
            const Icon = n.icon;
            const badge = n.badgeKey ? badges[n.badgeKey] : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `co-sidebar-item ${isActive ? "active" : ""}`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                <span className="flex-1">{n.label}</span>
                {badge > 0 && (
                  <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 bg-[#B45309] text-white rounded-sm">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-6 py-5 border-t co-hairline">
          <div className="text-xs text-muted-foreground mb-2">Signed in as</div>
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <div className="mt-2 co-label text-[#4338CA]">{user?.role || "owner"}</div>
          <button
            data-testid={T.logoutButton}
            onClick={doLogout}
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#4338CA] hover:text-[#3730A3] co-btn-press"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div
          data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
        />
      )}

      {/* Mobile top nav */}
      <div className="lg:hidden co-glass-header sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label="Open navigation menu"
            className="p-1.5 -ml-1.5 rounded-sm hover:bg-[#F4F4F5] co-btn-press"
          >
            {mobileNavOpen ? (
              <X className="h-5 w-5 text-[#0A0A0B]" strokeWidth={2} />
            ) : (
              <Menu className="h-5 w-5 text-[#0A0A0B]" strokeWidth={2} />
            )}
          </button>
          <img src="/collectone-logo.webp" alt="CollectOne" className="w-7 h-7 object-contain" />
          <span className="font-heading font-bold tracking-tight">CollectOne</span>
        </div>
        <button
          data-testid={T.logoutButton + "-mobile"}
          onClick={doLogout}
          className="text-xs font-semibold text-[#4338CA]"
        >
          Log out
        </button>
      </div>

      {/* Content */}
      <main className="min-w-0">
        {impersonating && (
          <div className="bg-[#0A0A0B] text-white px-6 lg:px-10 py-2.5 flex items-center justify-between text-xs">
            <span>
              <span className="font-bold">Impersonating</span> · viewing <span className="font-semibold">{impersonatingName || "workspace"}</span> as its owner
            </span>
            <button
              onClick={stopImpersonating}
              data-testid="stop-impersonating"
              className="font-bold underline hover:text-[#C7D2FE]"
            >
              Stop impersonating →
            </button>
          </div>
        )}
        <TrialBanner workspace={workspace} user={user} />
        <Outlet />
      </main>
      <InstallPWAPrompt />
    </div>
  );
}

function TrialBanner({ workspace, user }) {
  if (!workspace || user?.role === "platform_admin") return null;
  if (workspace.approval_status === "approved" || !workspace.trial_expires_at) return null;
  const exp = new Date(workspace.trial_expires_at);
  const msLeft = exp.getTime() - Date.now();
  const daysLeft = Math.ceil(msLeft / 86400000);
  if (msLeft <= 0) {
    // Trial expired — hard block banner
    return (
      <div
        data-testid="trial-banner-expired"
        className="bg-[#FEF2F2] border-b border-[#FCA5A5] text-[#7F1D1D] px-6 lg:px-10 py-3 flex items-center justify-between text-sm"
      >
        <span>
          <span className="font-bold">Your 3-day free trial has ended.</span> Product features are locked.
          Contact <a href="mailto:care@collectone.in" className="underline font-semibold">care@collectone.in</a> to upgrade and keep going.
        </span>
      </div>
    );
  }
  // Active trial — highlight days remaining
  return (
    <div
      data-testid="trial-banner-active"
      className="bg-[#F0FDF4] border-b border-[#BBF7D0] text-[#15803D] px-6 lg:px-10 py-2.5 flex items-center justify-between text-xs"
    >
      <span>
        <span className="font-bold">Free trial · {daysLeft} day{daysLeft > 1 ? "s" : ""} left.</span>
        {" "}Explore every feature — reminders, WhatsApp, Razorpay, SMS templates. Full access until {exp.toLocaleDateString()}.
      </span>
      <a href="mailto:care@collectone.in" className="font-bold underline hover:text-[#166534]">
        Upgrade &nbsp;→
      </a>
    </div>
  );
}
